import React, { type DetailedHTMLProps, type HTMLAttributes } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { useSelector } from 'react-redux';
import formatDate from '../../../helpers/formatDate';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import Button from '../../Button';
import CoreLink from '../../CoreLink';
import SkeletonWrapper from '../../SkeletonWrapper';
import useBlockCta from './hooks/useBlockCta';

import './style.scss';

type Props = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  hideMessage?: boolean
};

const BlockCta: React.FC<Props> = ({ className, hideMessage, ...restProps }) => {
  const { t } = useTranslation();
  const { dailyRegistration, setRecoveryPlanGoals, help } = useRoutes();
  const { variant, currentWeekNumber, goalToday } = useBlockCta();
  const { buildClass, joinClasses } = useClasses('BlockCta');

  const currentDomain = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], ''));
  const isInitialLoaded = useSelector<ImmutableRootState<boolean>, boolean>(
    (state) => state.getIn(['registrations', 'isInitialLoaded'], false),
  );

  return (
    <article
      {...restProps}
      className={joinClasses(buildClass(), className)}
    >
      <SkeletonWrapper active={!isInitialLoaded} className={buildClass('skeleton')}>
        <div className={buildClass('content')}>
          {(!hideMessage) && (
            <p className={buildClass('message')}>
              <Trans
                i18nKey={[`cta.${variant}.${currentDomain}`, `cta.${variant}.default`]}
                values={{
                  week: t(`cta.week_numbers.${currentWeekNumber}`).toLowerCase(),
                  day: formatDate(new Date(), 'dddd'),
                  verb: t(`cta.verbs.${currentDomain}`).toLowerCase(),
                  amount: t(`cta.units.${currentDomain}`, {
                    count: goalToday,
                  }),
                }}
              />
            </p>
          )}
        </div>

        <div className={buildClass('buttons')}>
          <Button className={buildClass('primary-button')} tertiary href={dailyRegistration} icon="writing">
            {t('buttons.daily_registration')}
          </Button>

          {variant === 'welcome' ? (
            <CoreLink to={setRecoveryPlanGoals} className={buildClass('secondary-button')}>
              {t('cta.create_plan')}
            </CoreLink>
          ) : (
            <CoreLink to={help} className={buildClass('secondary-button')}>
              {t(`cta.get_help.${currentDomain}`)}
            </CoreLink>
          )}
        </div>
      </SkeletonWrapper>
    </article>
  );
};

export default BlockCta;
